import { z } from 'zod';
import type { Numeric, NumericValue } from '@/utils/numeric';

// =============================================================================
// CORE ENTITIES
// =============================================================================

export interface CropType {
  id: number;
  name: string;
  description: string | null;
  parent_id: number | null;
}

export interface StakeholderType {
  id: number;
  organisation_id: number;
  stakeholder_type: string;
  verified: boolean;
  verified_at: string | null;
  created_at: string;
}

export interface CultivarProtection {
  id: number;
  cultivar_id: number;
  jurisdiction: string;
  type: 'pbr' | 'plant_patent' | 'cpvo' | 'pvr' | 'upov' | 'other';
  status: 'granted' | 'accepted' | 'filed' | 'under_examination' | 'ceased' | 'rejected' | string;
  registration_number: string | null;
  application_date: string | null;
  acceptance_date: string | null;
  grant_date: string | null;
  expiry_date: string | null;
  authority: string | null;
  source_url: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export type CultivarProtectionFormData = Omit<CultivarProtection, 'id' | 'cultivar_id' | 'created_at' | 'updated_at'>;

export type CultivarProgramType =
  | 'annual_tree_royalty'
  | 'production_royalty'
  | 'per_sale_royalty'
  | 'other';

export type RoyaltyUnit =
  | 'per_tree_per_year'
  | 'per_tonne_produced'
  | 'per_kg_produced'
  | 'percent_of_sale'
  | 'fixed_per_sale'
  | 'other';

export type ReportingCadence =
  | 'annual'
  | 'biannual'
  | 'quarterly'
  | 'monthly'
  | 'per_sale'
  | 'custom';

export interface CultivarProgram {
  id: number;
  organisation_id: number;
  organisation_name?: string;
  name: string;
  program_type: CultivarProgramType;
  custom_type_label: string | null;
  description: string | null;
  active: boolean;
  royalty_rate: Numeric;
  royalty_unit: RoyaltyUnit | null;
  currency: string;
  minimum_royalty: Numeric;
  reporting_cadence: ReportingCadence | null;
  reporting_due_month: number | null;
  reporting_due_day: number | null;
  default_terms: string | null;
  default_terms_template_id: number | null;
  royalty_start_leaf: number | null;
  reporting_start_leaf: number | null;
  default_yield_kg_per_tree_per_year: Numeric;
  default_yield_tonnes_per_ha_per_year: Numeric;
  rules: Record<string, unknown>;
  share_with_program_orchards?: boolean;
  visibility_config?: VisibilityConfig;
  logo_url: string | null;
  hero_image_url: string | null;
  tagline: string | null;
  marketing_description: string | null;
  website_terms: string | null;
  brand_color: string | null;
  external_url: string | null;
  public: boolean;
  cultivar_count?: NumericValue;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Viewer-tier visibility
// ---------------------------------------------------------------------------

export type ViewerTier =
  | 'owner'
  | 'breeder'
  | 'region_licensor_breeder'
  | 'licensor'
  | 'production_licensee'
  | 'nursery_licensee'
  | 'program_orchard'
  | 'open'
  | 'public'
  | 'none';

export type VisibilityAudience =
  | 'nursery_licensee'
  | 'production_licensee'
  | 'program_orchard'
  | 'public';

export interface VisibilityTierConfig {
  fields?: string[];
  attribute_categories?: string[];
}

export type VisibilityConfig = Partial<Record<VisibilityAudience, VisibilityTierConfig>>;

// ---------------------------------------------------------------------------
// Extensible attributes (defs + observation values)
// ---------------------------------------------------------------------------

export type AttributeDataType = 'number' | 'text' | 'enum' | 'boolean' | 'date' | 'range';

export interface AttributeDefinition {
  id: number;
  slug: string;
  display_name: string;
  category: string;
  data_type: AttributeDataType;
  unit: string | null;
  enum_options: string[] | null;
  is_seasonal: boolean;
  visibility_default: 'owner' | 'licensee' | 'production_licensee' | 'program_orchard' | 'public';
  description: string | null;
  created_by_org_id: number | null;
  is_global?: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AttributeValue {
  id: number;
  cultivar_id: number;
  attribute_id: number;
  value: unknown;
  season_year: number | null;
  latitude: number | null;
  longitude: number | null;
  farm_id: number | null;
  /** Named on the reading itself. Outranks the farm's region in the roll-up. */
  growing_region_id: number | null;
  observed_by_org_id: number;
  observed_by_user_id: number | null;
  observer_role: 'owner' | 'evaluator' | 'grower';
  source_notes: string | null;
  attribute_slug?: string;
  attribute_display_name?: string;
  attribute_category?: string;
  attribute_data_type?: AttributeDataType;
  attribute_unit?: string | null;
  attribute_visibility_default?: string;
  observer_org_name?: string;
  farm_name?: string | null;
  /** The reading's own region, or its farm's — whichever the roll-up will use. */
  growing_region_name?: string | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Evaluation sessions
// ---------------------------------------------------------------------------
// One orchard visit: many attribute readings, photos and follow-ups captured
// together. The individual readings are still AttributeValue rows, now carrying
// an evaluation_id.

export type SeasonStage =
  | 'dormant' | 'bud_break' | 'flowering' | 'fruit_set'
  | 'cell_division' | 'pre_harvest' | 'harvest' | 'post_harvest';

export const SEASON_STAGES: SeasonStage[] = [
  'dormant', 'bud_break', 'flowering', 'fruit_set',
  'cell_division', 'pre_harvest', 'harvest', 'post_harvest',
];

export const SEASON_STAGE_LABELS: Record<SeasonStage, string> = {
  dormant: 'Dormant',
  bud_break: 'Bud break',
  flowering: 'Flowering',
  fruit_set: 'Fruit set',
  cell_division: 'Cell division',
  pre_harvest: 'Pre-harvest',
  harvest: 'Harvest',
  post_harvest: 'Post-harvest',
};

/** 'S' below the equator, 'N' above. Null when location is unknown. */
export type Hemisphere = 'N' | 'S';

/** Most recent recorded harvest date for a cultivar, per hemisphere. */
export interface HarvestReferences {
  N: string | null;
  S: string | null;
  /** Observations with no coordinates — can't be attributed to a hemisphere. */
  unknown: string | null;
}

/** Where an evaluation sits relative to the cultivar's expected harvest. */
export interface SeasonPosition {
  /** The maturity_date observation used as the reference, if one exists. */
  reference_maturity_date: string | null;
  /** Negative = before harvest. Null when the cultivar has no maturity data. */
  days_from_harvest: number | null;
  season_stage: SeasonStage | null;
  /** Hemisphere the reference harvest date came from. */
  reference_hemisphere: Hemisphere | null;
  /**
   * True when no same-hemisphere harvest date existed and we borrowed the
   * other hemisphere's — the figure is then roughly half a year out.
   */
  reference_is_cross_hemisphere: boolean;
  hemisphere?: Hemisphere | null;
}

export interface EvaluationFollowup {
  id: number;
  evaluation_id: number;
  cultivar_id: number;
  organisation_id: number;
  due_date: string;
  focus_attribute_ids: number[];
  note: string | null;
  assigned_to_user_id: number | null;
  created_by_user_id: number | null;
  status: 'pending' | 'completed' | 'dismissed';
  notified_at: string | null;
  completed_at: string | null;
  completed_evaluation_id: number | null;
  created_at: string;
  updated_at: string;
  // Joined
  cultivar_name?: string;
  assigned_to_name?: string | null;
  source_evaluated_on?: string;
  source_season_stage?: SeasonStage | null;
  overdue?: boolean;
}

export interface EvaluationMedia {
  id: number;
  kind: string;
  file_name: string;
  file_type: string | null;
  caption: string | null;
  visibility: string;
  latitude: number | null;
  longitude: number | null;
  taken_at: string | null;
  created_at: string;
}

export interface CultivarEvaluation {
  id: number;
  cultivar_id: number;
  client_uuid: string;
  evaluated_on: string;
  season_year: number | null;
  reference_maturity_date: string | null;
  days_from_harvest: number | null;
  season_stage: SeasonStage | null;
  farm_id: number | null;
  block_id: number | null;
  cultivar_area_id: number | null;
  /**
   * Which climate area the visit sat in. This is what lets a maturity_date
   * reading roll up into cultivar_region_maturity; without it (and without one
   * on the named farm) the observation is unattributable and never counted.
   */
  growing_region_id: number | null;
  latitude: number | null;
  longitude: number | null;
  location_label: string | null;
  overall_score: number | null;
  summary: string | null;
  conditions_notes: string | null;
  status: 'draft' | 'submitted';
  observed_by_org_id: number;
  observed_by_user_id: number | null;
  observer_role: 'owner' | 'evaluator' | 'grower';
  /** Derived from the captured latitude at write time. */
  hemisphere: Hemisphere | null;
  created_at: string;
  updated_at: string;
  // Present only when the list was requested with `normalise_to`. The original
  // `evaluated_on` is always the date actually recorded; this is its equivalent
  // in the requested hemisphere.
  translated_date?: string | null;
  /** True when translation fell back to a flat six-month shift. */
  translated_is_approximate?: boolean;
  translated_to_hemisphere?: Hemisphere | null;
  // Joined / aggregated
  observer_org_name?: string;
  observer_user_name?: string | null;
  farm_name?: string | null;
  block_name?: string | null;
  cultivar_area_name?: string | null;
  growing_region_name?: string | null;
  value_count?: string | number;
  photo_count?: string | number;
  pending_followup_count?: string | number;
  // Populated only by the detail endpoint
  values?: AttributeValue[];
  media?: EvaluationMedia[];
  followups?: EvaluationFollowup[];
}

/** Payload for creating an evaluation. `client_uuid` is minted on-device. */
export interface EvaluationInput {
  client_uuid: string;
  evaluated_on: string;
  season_year?: number | null;
  season_stage?: SeasonStage | null;
  farm_id?: number | null;
  block_id?: number | null;
  cultivar_area_id?: number | null;
  growing_region_id?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  location_label?: string | null;
  overall_score?: number | null;
  summary?: string | null;
  conditions_notes?: string | null;
  status?: 'draft' | 'submitted';
  /** Fallback when GPS wasn't captured; the server prefers latitude. */
  hemisphere?: Hemisphere | null;
  values: { attribute_id: number; value: unknown; source_notes?: string | null }[];
  followups?: {
    due_date: string;
    focus_attribute_ids?: number[];
    note?: string | null;
    assigned_to_user_id?: number | null;
  }[];
}

// ---------------------------------------------------------------------------
// Growing regions & maturity
// ---------------------------------------------------------------------------
// A growing region is a climate/harvest-timing area. It is NOT the ISO 3166-2
// `region_code` used for licensing — Batlow and Sydney share AU-NSW and harvest
// weeks apart, so the two cannot be the same field.

export interface GrowingRegion {
  id: number;
  name: string;
  country: string | null;
  /** Optional ISO 3166-2 tag for licensing cross-reference only. */
  region_code: string | null;
  centroid_latitude: number | null;
  centroid_longitude: number | null;
  boundary_geometry: unknown | null;
  hemisphere: Hemisphere | null;
  elevation_m: number | null;
  notes: string | null;
  created_by_org_id: number | null;
  /** True for shared regions every organisation can use. */
  is_global?: boolean;
  active: boolean;
  maturity_record_count?: string | number;
  created_at: string;
  updated_at: string;
}

export type MaturitySource = 'manual' | 'observed' | 'calculated';

/**
 * What a pick window's end rests on, most specific first: the entry's own last
 * pick, a window length stated on the entry, the cultivar's harvest_window_days
 * readings, or the server's default. Only 'recorded' is a measured end.
 */
export type PickWindowSource = 'recorded' | 'record' | 'cultivar' | 'default';

export interface CultivarRegionMaturity {
  id: number;
  cultivar_id: number;
  growing_region_id: number;
  season_year: number;
  first_pick_date: string;
  last_pick_date: string | null;
  harvest_window_days: number | null;
  /** Stored records are only ever manual or observed. */
  source: Exclude<MaturitySource, 'calculated'>;
  observation_count: number;
  notes: string | null;
  organisation_id: number;
  created_at: string;
  updated_at: string;
  // Joined
  cultivar_name?: string;
  region_name?: string;
  region_country?: string | null;
  hemisphere?: Hemisphere | null;
  organisation_name?: string;
}

/** "Region B picks N days after region A." Positive = B later. */
export interface RegionOffset {
  region_a_id: number;
  region_b_id: number;
  median_offset_days: number;
  mean_offset_days: number;
  stddev_days: number;
  sample_size: number;
  distinct_cultivars: number;
  distinct_seasons: number;
  /** 0..1, falling with small samples and with disagreement between them. */
  confidence: number;
}

/** Negative deviation = season running early in that region. */
export interface SeasonIndex {
  growing_region_id: number;
  season_year: number;
  deviation_days: number;
  direction: 'early' | 'late' | 'typical';
  cultivar_count: number;
  stddev_days: number;
  confidence: number;
}

/**
 * One bar on the maturity timeline: a cultivar's pick window in one region in
 * one season, carrying enough of its cultivar and region to be read on its own.
 *
 * Crop type comes down denormalised because the chart colours by it, and a
 * second lookup per bar would mean the colours arrive after the bars.
 */
export interface MaturityChartEntry {
  cultivar_id: number;
  cultivar_name: string | null;
  crop_type_id: number | null;
  crop_type_name: string | null;
  /** Null when the crop type is itself top-level. */
  crop_type_parent_id: number | null;
  crop_type_parent_name: string | null;
  growing_region_id: number;
  region_name: string | null;
  region_country: string | null;
  season_year: number;
  first_pick_date: string;
  last_pick_date: string | null;
  harvest_window_days: number | null;
  /**
   * Where the bar ends. Always set by the server: a single maturity reading is
   * only a start, so its end comes from `window_source`.
   */
  window_end_date?: string;
  window_days?: number;
  window_source?: PickWindowSource;
  source: MaturitySource;
  observation_count?: number;
  /** Null for observed rows; 0..1 for calculated ones. */
  confidence: number | null;
  uncertainty_days: number;
  notes?: string | null;
  /** Set only on calculated rows — which region the estimate came from. */
  derived_from_region_id?: number;
  offset_days?: number;
}

/** A filter option that has data behind it. */
export interface MaturityChartRegionFacet {
  id: number;
  name: string;
  country: string | null;
  hemisphere: Hemisphere | null;
  record_count: number;
}

export interface MaturityChartCropTypeFacet {
  id: number;
  name: string;
  parent_id: number | null;
  parent_name: string | null;
  record_count: number;
}

/**
 * What the filter controls may offer.
 *
 * Independent of the filters currently applied — the crop-type list is also
 * what assigns chart colours, and a list that shrank as you filtered would
 * repaint every surviving crop type on each click.
 */
export interface MaturityChartFacets {
  seasons: number[];
  regions: MaturityChartRegionFacet[];
  crop_types: MaturityChartCropTypeFacet[];
}

export interface MaturityChartFilters {
  seasonYears?: number[];
  /** Every season with data; overrides seasonYears. */
  allSeasons?: boolean;
  regionIds?: number[];
  cropTypeIds?: number[];
  fromDate?: string;
  toDate?: string;
  includeEstimates?: boolean;
}

export interface MaturityChart {
  entries: MaturityChartEntry[];
  facets: MaturityChartFacets;
  /** Only ever set for a single region in a single season — the shape the statistic describes. */
  season_index: SeasonIndex | null;
  filters_applied: {
    season_years: number[];
    /** Absent from servers older than the all-seasons option. */
    all_seasons?: boolean;
    region_ids: number[] | null;
    crop_type_ids: number[] | null;
    from_date: string | null;
    to_date: string | null;
    include_estimates: boolean;
    online_only: boolean;
  };
  /** True when the server cut the result; `total_matched` is the uncut count. */
  truncated: boolean;
  total_matched: number;
}

// ---------------------------------------------------------------------------
// Evaluator grants
// ---------------------------------------------------------------------------

export interface EvaluatorGrant {
  id: number;
  cultivar_id: number | null;
  program_id: number | null;
  evaluator_org_id: number;
  evaluator_org_name?: string;
  granted_by_org_id: number;
  granted_by_user_id: number | null;
  status: 'active' | 'revoked';
  notes: string | null;
  cultivar_name?: string;
  program_name?: string;
  granted_at: string;
  revoked_at: string | null;
}

// ---------------------------------------------------------------------------
// Cultivar media
// ---------------------------------------------------------------------------

export type CultivarMediaKind = 'photo' | 'document' | 'label' | 'lab_report' | 'evaluator_pdf';
export type CultivarMediaVisibility = 'public' | 'licensed' | 'owner';

export interface CultivarMedia {
  id: number;
  cultivar_id: number;
  kind: CultivarMediaKind;
  file_path: string;
  file_name: string;
  file_type: string | null;
  caption: string | null;
  sort_order: number;
  is_primary: boolean;
  visibility: CultivarMediaVisibility;
  uploaded_by_org_id: number;
  uploaded_by_org_name?: string;
  uploaded_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * Whether FactreeTech has checked the owning organisation's claim to the
 * rights in a protected cultivar (HHNodeServer `cultivar_rights_state`).
 * `not_required`: nothing protected is claimed. `pending`: not checked yet,
 * or the owner, breeder or a protection number changed since it was.
 */
export type CultivarRightsState = 'not_required' | 'pending' | 'verified' | 'rejected';

export interface Cultivar {
  id: number;
  name: string;
  trade_name: string | null;
  botanical_name: string | null;
  species: string | null;
  crop_type_id: number | null;
  // What the cultivar is bred to be, not where it sits on a given tree —
  // position belongs to the planting record. 'dual' answers to either role, so
  // role filters match it with IN (role, 'dual') rather than equality. The
  // create/edit form only authors the first two; 'dual' is preserved on edit
  // but not offered as a new choice.
  cultivar_role: 'scion' | 'rootstock' | 'dual';
  crop_type_name?: string;
  crop_type_description?: string;
  crop_type_parent_id?: number | null;
  crop_type_parent_name?: string;
  protection_status: 'pending' | 'protected' | 'expired' | 'open';
  rights_state?: CultivarRightsState | null;
  /** The owner's view only: why FactreeTech decided as it did. */
  rights_review_note?: string | null;
  protections?: CultivarProtection[];
  protection_count?: NumericValue;
  breeder_org_id: number | null;
  breeder_org_name?: string;
  breeder_name?: string | null;
  origin_country: string | null;
  year_bred: number | null;
  chill_requirement_portions?: number | null;
  description: string | null;
  status: 'active' | 'inactive' | 'withdrawn' | 'expired';
  is_public: boolean;
  release_state?: 'breeder_only' | 'region_licensor' | 'public';
  primary_display_synonym_id?: number | null;
  legacy_visibility_grace?: boolean;
  created_by_org_id: number;
  created_by_org_name?: string;
  created_by_user_id: number | null;
  programs?: CultivarProgram[];
  program_id?: number | null;
  visibility_config?: VisibilityConfig;
  viewer_tier?: ViewerTier;
  viewer_is_owner?: boolean;
  viewer_has_license?: boolean;
  is_open_to_propagate?: boolean;
  media?: CultivarMedia[];
  attribute_values?: AttributeValue[];
  // PBR workflow fields
  is_pbr_candidate?: boolean;
  first_sale_date_au?: string | null;
  first_sale_date_overseas?: string | null;
  first_sale_country_overseas?: string | null;
  us_patent_number?: string | null;
  breeding_history?: string | null;
  distinctness_summary?: string | null;
  // Public-website publish fields (drives website.factree.com.au).
  show_on_website?: boolean;
  website_tagline?: string | null;
  website_description?: string | null;
  website_hero_media_id?: number | null;
  website_sort_order?: number;
  website_published_at?: string | null;
  // Talking points for the people who sell it. Staff-only until published.
  sales_profile?: CultivarSalesProfile | null;
  sales_profile_public?: boolean;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// CULTIVAR INFO (the customer blurb and the staff talking points)
// =============================================================================

export interface CultivarComparison {
  /** One of the organisation's own cultivars, or null for a name only. */
  cultivar_id: number | null;
  name: string;
  difference: string;
}

/**
 * What staff read to speak to a customer. Shape is fixed server-side
 * (HHNodeServer cultivarInfoService.sanitiseContent). A rootstock has no
 * `fruit`.
 */
export interface CultivarSalesProfile {
  summary: string | null;
  fruit: string | null;
  season: string | null;
  tree: string | null;
  growing: string | null;
  best_for: string[];
  selling_points: string[];
  watch_outs: string[];
  compared_with: CultivarComparison[];
  // Where the research came from. Absent on what the public website is given.
  sources?: { title: string; url: string }[];
  confidence?: 'high' | 'medium' | 'low' | null;
  researched_at?: string | null;
  researched_by_model?: string | null;
  reviewed_at?: string | null;
  reviewed_by_user_id?: number | null;
}

/** AI research for one cultivar: a proposal to review. Nothing is saved by it. */
export type CultivarInfoProposal =
  | {
      found: true;
      description: string | null;
      website_tagline: string | null;
      profile: CultivarSalesProfile;
      warnings: string[];
      compared_against: { id: number; name: string }[];
      model: string;
    }
  | { found: false; message: string };

export interface CultivarInfo {
  cultivar_id: number;
  description: string | null;
  website_tagline: string | null;
  sales_profile: CultivarSalesProfile | null;
  sales_profile_public: boolean;
}

export interface CultivarInfoSave {
  description?: string | null;
  website_tagline?: string | null;
  sales_profile?: CultivarSalesProfile | null;
  sales_profile_public?: boolean;
}

// =============================================================================
// PBR WORKFLOW
// =============================================================================

export type PbrStage =
  | 'candidate'
  | 'part1_prep'
  | 'part1_submitted'
  | 'accepted'
  | 'trial_in_progress'
  | 'part2_prep'
  | 'part2_submitted'
  | 'granted'
  | 'ceased'
  | 'rejected'
  | 'withdrawn';

export const PBR_STAGE_LABELS: Record<PbrStage, string> = {
  candidate: 'Candidate',
  part1_prep: 'Part 1 in preparation',
  part1_submitted: 'Part 1 submitted',
  accepted: 'Accepted',
  trial_in_progress: 'Growing trial in progress',
  part2_prep: 'Part 2 in preparation',
  part2_submitted: 'Part 2 submitted',
  granted: 'Granted',
  ceased: 'Ceased',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

export type PbrApplicationType = 'pbr' | 'plant_patent' | 'cpvo' | 'pvr';

export interface QualifiedPerson {
  id: number;
  organisation_id: number;
  user_id: number | null;
  linked_user_email?: string | null;
  linked_username?: string | null;
  full_name: string;
  email: string | null;
  phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
  accreditation_number: string | null;
  accredited_species: string[];
  is_external_consultant: boolean;
  active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface PbrApplication {
  id: number;
  cultivar_id: number;
  cultivar_name?: string;
  cultivar_trade_name?: string | null;
  botanical_name?: string | null;
  species?: string | null;
  organisation_id: number;
  jurisdiction: string;
  application_type: PbrApplicationType;
  stage: PbrStage;

  applicant_name: string | null;
  applicant_address: string | null;
  applicant_country: string | null;
  applicant_state: string | null;
  applicant_postcode: string | null;

  agent_org_id: number | null;
  agent_name: string | null;
  agent_address: string | null;
  agent_contact_person: string | null;
  agent_phone: string | null;
  agent_email: string | null;

  qualified_person_id: number | null;
  qualified_person_name?: string | null;
  qualified_person_email?: string | null;
  qualified_person_accreditation?: string | null;
  qp_nomination_functions: Record<string, boolean>;

  part1_due_date: string | null;
  part1_submitted_date: string | null;
  acceptance_date: string | null;
  grant_date: string | null;
  expiry_date: string | null;
  registration_number: string | null;
  fee_paid_cents: number | null;
  fee_paid_date: string | null;

  growing_trial_required: boolean;
  growing_trial_waiver_reason: string | null;
  upov_description_available: boolean;
  overseas_reference_application: string | null;

  // Cultivar-sourced convenience fields (populated by getById join)
  first_sale_date_au?: string | null;
  first_sale_date_overseas?: string | null;
  first_sale_country_overseas?: string | null;
  us_patent_number?: string | null;
  breeding_history?: string | null;
  distinctness_summary?: string | null;
  breeder_name?: string | null;

  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface PbrApplicationEvent {
  id: number;
  pbr_application_id: number;
  event_date: string;
  event_type:
    | 'submission'
    | 'correspondence'
    | 'trial_start'
    | 'trial_complete'
    | 'status_change'
    | 'payment'
    | 'document_generated'
    | 'note';
  description: string | null;
  document_id: number | null;
  document_title?: string | null;
  document_filename?: string | null;
  old_stage: PbrStage | null;
  new_stage: PbrStage | null;
  created_by_user_id: number | null;
  created_by_username?: string | null;
  created_by_email?: string | null;
  created_at: string;
}

export interface PbrCandidate {
  id: number;
  name: string;
  trade_name: string | null;
  botanical_name: string | null;
  species: string | null;
  first_sale_date_au: string | null;
  first_sale_date_overseas: string | null;
  first_sale_country_overseas: string | null;
  us_patent_number: string | null;
  year_bred: number | null;
  part1_deadline: string | null;
  applications: Array<{
    id: number;
    jurisdiction: string;
    application_type: PbrApplicationType;
    stage: PbrStage;
    part1_due_date: string | null;
  }> | null;
}

export interface PbrDashboardSummary {
  byStage: Partial<Record<PbrStage, number>>;
  upcomingDeadlines: Array<{
    id: number;
    cultivar_name: string;
    cultivar_trade_name: string | null;
    jurisdiction: string;
    application_type: PbrApplicationType;
    stage: PbrStage;
    part1_due_date: string;
  }>;
  trialsNeeded: Array<{
    id: number;
    cultivar_name: string;
    jurisdiction: string;
    stage: PbrStage;
  }>;
  candidatesWithoutApplication: PbrCandidate[];
}

export type LicenseTier =
  | 'nursery_master_sublicence'
  | 'nursery_propagation'
  | 'nursery_sales'
  | 'orchard_planting';

// Short names for a licence's tier, for lists and cards. A sales licence lets a
// nursery that propagates the cultivar enter orders with its licence holder
// (docs/cultivar-sales-licence-plan.md).
export const LICENSE_TIER_SHORT: Record<LicenseTier, string> = {
  nursery_master_sublicence: 'Master Sublicence',
  nursery_propagation: 'Nursery Propagation',
  nursery_sales: 'Nursery Sales',
  orchard_planting: 'Orchard Planting',
};

export function licenseTierShort(tier: string | null | undefined): string {
  return LICENSE_TIER_SHORT[tier as LicenseTier] ?? 'Cultivar';
}

export interface RegionalLicense {
  id: number;
  cultivar_id: number;
  cultivar_name?: string;
  cultivar_trade_name?: string;
  /** Whether FactreeTech has checked the licensor's rights in the cultivar. Null on a master. */
  cultivar_rights_state?: CultivarRightsState | null;
  // The rootstock these licensed trees are worked on. It may be protected by a
  // different rights holder, licensed separately or paid for at the nursery.
  rootstock_cultivar_id?: number | null;
  rootstock_cultivar_name?: string | null;
  rootstock_cultivar_trade_name?: string | null;
  rootstock_protection_status?: string | null;
  licensor_org_id: number;
  licensor_org_name?: string;
  licensee_org_id: number;
  licensee_org_name?: string;
  license_tier: LicenseTier;
  region: string | null;
  country: string | null;
  license_number: string | null;
  start_date: string | null;
  end_date: string | null;
  // 'superseded': an unsigned agreement withdrawn because its contract moved
  // to a new version (or was withdrawn); superseded_by_license_id names the
  // reissued one when there is one.
  status: 'pending_confirmation' | 'active' | 'expired' | 'suspended' | 'terminated' | 'rejected' | 'superseded';
  initiated_by: 'licensor' | 'licensee';
  licensor_confirmed: boolean;
  licensor_confirmed_at: string | null;
  licensee_confirmed: boolean;
  licensee_confirmed_at: string | null;
  signed_at: string | null;
  signer_name: string | null;
  signature_method: 'e_sig' | 'uploaded' | null;
  terms: string | null;
  max_hectares: Numeric;
  max_trees: number | null;
  tolerance_pct: Numeric;
  supplying_nursery_org_id: number | null;
  template_id: number | null;
  /** The contract version this agreement was written on; null before versioning. */
  template_version?: number | null;
  contract_family_id?: number | null;
  /** SHA-256 of the agreement's text, fixed when it was sent. */
  content_hash?: string | null;
  sublicence_group_id?: number | null;
  superseded_by_license_id?: number | null;
  void_reason?: string | null;
  royalty_structure: Record<string, unknown>;
  suspended_at: string | null;
  suspension_reason_public: string | null;
  terminated_at: string | null;
  termination_reason_public: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductionDeclaration {
  id: number;
  license_id: number;
  cultivar_id: number;
  cultivar_name?: string;
  declaring_org_id: number;
  declaring_org_name?: string;
  licensor_org_name?: string;
  license_tier?: LicenseTier;
  season_year: number;
  trees_planted: number | null;
  hectares_planted: Numeric;
  // Composite tree. rootstock_cultivar_id is the real reference; `rootstock`
  // remains the free-text fallback.
  rootstock: string | null;
  rootstock_cultivar_id?: number | null;
  rootstock_cultivar_name?: string | null;
  rootstock_protection_status?: string | null;
  interstem_cultivar_id?: number | null;
  planting_date: string | null;
  estimated_production_tonnes: Numeric;
  actual_production_tonnes: Numeric;
  packed_tonnes: Numeric;
  pack_formats: Array<{ format: string; count: number }>;
  marketed_tonnes: Numeric;
  markets: Array<{ market: string; tonnes: number }>;
  status: 'declared' | 'acknowledged' | 'queried' | 'revised' | 'rejected';
  acknowledged_by_org_id: number | null;
  acknowledged_by_org_name?: string;
  acknowledged_by_user_id: number | null;
  acknowledged_at: string | null;
  acknowledgment_notes: string | null;
  query_message: string | null;
  queried_at: string | null;
  revision_of: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface EntitlementCertificate {
  id: number;
  license_id: number;
  declaration_id: number | null;
  certificate_number: string;
  verification_token: string;
  cultivar_name: string;
  licensee_org_name: string;
  licensor_org_name: string;
  license_tier: string;
  region: string | null;
  season_year: number | null;
  declared_trees: number | null;
  declared_hectares: Numeric;
  declared_production_tonnes: Numeric;
  declared_packed_tonnes: Numeric;
  declared_marketed_tonnes: Numeric;
  issued_at: string;
  valid_from: string;
  valid_until: string;
  status: 'active' | 'expired' | 'revoked' | 'superseded';
  revoked_at: string | null;
  revoked_reason: string | null;
  pdf_file_path: string | null;
  pdf_generated_at: string | null;
  created_at: string;
}

export interface NurseryCultivar {
  id: number;
  nursery_org_id: number;
  nursery_org_name?: string;
  cultivar_id: number;
  cultivar_name?: string;
  cultivar_trade_name?: string;
  availability_status: 'available' | 'limited' | 'pre_order' | 'unavailable';
  rootstock_options: Array<{ name: string; code?: string; description?: string }>;
  min_order_qty: number | null;
  lead_time_months: number | null;
  price_indication: Record<string, unknown>;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ComplianceDocument {
  id: number;
  organisation_id: number;
  document_type: string;
  related_entity_type: string | null;
  related_entity_id: number | null;
  title: string;
  file_name: string;
  file_path: string;
  file_type: string | null;
  file_size_bytes: Numeric;
  issue_date: string | null;
  expiry_date: string | null;
  status: 'active' | 'expired' | 'superseded' | 'draft';
  certification_body: string | null;
  certificate_number: string | null;
  uploaded_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface ManagementGuide {
  id: number;
  cultivar_id: number;
  cultivar_name?: string;
  published_by_org_id: number;
  published_by_org_name?: string;
  published_by_user_id: number | null;
  title: string;
  category: string;
  content: string;
  region: string | null;
  season: string | null;
  visibility: 'public' | 'licensed_growers' | 'organisation_only';
  version: string;
  is_latest: boolean;
  created_at: string;
  updated_at: string;
}

export interface ComplianceAlert {
  id: number;
  organisation_id: number;
  alert_type: string;
  related_entity_type: string;
  related_entity_id: number;
  title: string;
  message: string | null;
  due_date: string;
  severity: 'info' | 'warning' | 'critical';
  status: 'pending' | 'acknowledged' | 'resolved' | 'dismissed';
  acknowledged_by: number | null;
  acknowledged_at: string | null;
  created_at: string;
}

export interface AuditLogEntry {
  id: number;
  entity_type: string;
  entity_id: number;
  action: string;
  changes: Record<string, unknown>;
  changed_by_user_id: number | null;
  changed_by_username?: string;
  changed_by_org_id: number | null;
  changed_by_org_name?: string;
  ip_address: string | null;
  created_at: string;
}

export interface ComplianceDashboard {
  alerts: { critical: number; warning: number; info: number; total: number };
  total_cultivars: number;
  active_licenses: number;
  pending_licenses: number;
  expiring_licenses_30d: number;
  expiring_documents_30d: number;
}

export interface CertificateVerification {
  revoked_at: any;
  revoked_reason: any;
  certificate_number: string;
  status: string;
  is_valid: boolean;
  cultivar_name: string;
  licensee_org_name: string;
  licensor_org_name: string;
  license_tier: string;
  region: string | null;
  season_year: number | null;
  declared_trees: number | null;
  declared_hectares: Numeric;
  declared_production_tonnes: Numeric;
  declared_packed_tonnes: Numeric;
  declared_marketed_tonnes: Numeric;
  valid_from: string;
  valid_until: string;
  issued_at: string;
  license_status: string;
}

// =============================================================================
// ZOD SCHEMAS
// =============================================================================

export const cultivarSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  trade_name: z.string().max(255).optional().nullable(),
  botanical_name: z.string().max(255).optional().nullable(),
  species: z.string().max(255).optional().nullable(),
  crop_type_id: z.number().int().optional().nullable(),
  // Three values, not two. 'dual' means the cultivar answers to either role:
  // the interstem backfill selects `cultivar_role IN ('rootstock', 'dual')`
  // and orchardService validates a chosen rootstock the same way, so the edit
  // form has to round-trip it. Narrowing the form to two values meant opening
  // a dual-purpose cultivar and saving it silently demoted it to a scion,
  // dropping it out of every rootstock picker.
  cultivar_role: z.enum(['scion', 'rootstock', 'dual'], { message: 'Cultivar role is required' }),
  protection_status: z.enum(['pending', 'protected', 'expired', 'open']).optional(),
  breeder_org_id: z.number().int().optional().nullable(),
  breeder_name: z.string().max(255).optional().nullable(),
  origin_country: z.string().max(100).optional().nullable(),
  year_bred: z.number().int().min(1800).max(2100).optional().nullable(),
  chill_requirement_portions: z.number().int().min(0).max(200).optional().nullable(),
  description: z.string().optional().nullable(),
  is_public: z.boolean().optional(),
  // PBR workflow
  is_pbr_candidate: z.boolean().optional(),
  first_sale_date_au: z.string().optional().nullable(),
  first_sale_date_overseas: z.string().optional().nullable(),
  first_sale_country_overseas: z.string().max(100).optional().nullable(),
  us_patent_number: z.string().max(100).optional().nullable(),
  breeding_history: z.string().optional().nullable(),
  distinctness_summary: z.string().optional().nullable(),
});

export const qualifiedPersonSchema = z.object({
  user_id: z.number().int().optional().nullable(),
  full_name: z.string().min(1, 'Name is required').max(255),
  email: z.string().email('Invalid email').max(255).optional().nullable().or(z.literal('')),
  phone: z.string().max(50).optional().nullable(),
  address_line1: z.string().max(255).optional().nullable(),
  address_line2: z.string().max(255).optional().nullable(),
  suburb: z.string().max(100).optional().nullable(),
  state: z.string().max(50).optional().nullable(),
  postcode: z.string().max(20).optional().nullable(),
  country: z.string().max(100).optional().nullable(),
  accreditation_number: z.string().max(100).optional().nullable(),
  accredited_species: z.array(z.string()).optional(),
  is_external_consultant: z.boolean().optional(),
  active: z.boolean().optional(),
  notes: z.string().optional().nullable(),
});

export type QualifiedPersonFormData = z.infer<typeof qualifiedPersonSchema>;

export const pbrApplicationCreateSchema = z.object({
  cultivar_id: z.number().int({ message: 'Cultivar is required' }),
  jurisdiction: z.enum(['AU', 'US', 'EU', 'NZ', 'INT', 'JP', 'KR', 'ZA', 'CL', 'OTHER'], {
    message: 'Jurisdiction is required',
  }),
  application_type: z.enum(['pbr', 'plant_patent', 'cpvo', 'pvr'], {
    message: 'Application type is required',
  }),
  applicant_name: z.string().max(255).optional().nullable(),
  applicant_address: z.string().optional().nullable(),
  applicant_country: z.string().max(100).optional().nullable(),
  applicant_state: z.string().max(100).optional().nullable(),
  applicant_postcode: z.string().max(20).optional().nullable(),
  agent_org_id: z.number().int().optional().nullable(),
  agent_name: z.string().max(255).optional().nullable(),
  agent_address: z.string().optional().nullable(),
  agent_contact_person: z.string().max(255).optional().nullable(),
  agent_phone: z.string().max(50).optional().nullable(),
  agent_email: z.string().email('Invalid email').max(255).optional().nullable().or(z.literal('')),
  qualified_person_id: z.number().int().optional().nullable(),
  qp_nomination_functions: z.record(z.string(), z.boolean()).optional(),
  upov_description_available: z.boolean().optional(),
  overseas_reference_application: z.string().max(100).optional().nullable(),
  notes: z.string().optional().nullable(),
});

export type PbrApplicationCreateData = z.infer<typeof pbrApplicationCreateSchema>;

export type CultivarFormData = z.infer<typeof cultivarSchema>;

export const PROGRAM_TYPE_LABELS: Record<CultivarProgramType, string> = {
  annual_tree_royalty: 'Annual Tree Royalty',
  production_royalty: 'Production Royalty',
  per_sale_royalty: 'Per-Sale Royalty',
  other: 'Other / Custom',
};

export const ROYALTY_UNIT_LABELS: Record<RoyaltyUnit, string> = {
  per_tree_per_year: 'Per tree, per year',
  per_tonne_produced: 'Per tonne produced',
  per_kg_produced: 'Per kg produced',
  percent_of_sale: '% of sale price',
  fixed_per_sale: 'Fixed amount per sale',
  other: 'Other',
};

export const REPORTING_CADENCE_LABELS: Record<ReportingCadence, string> = {
  annual: 'Annually',
  biannual: 'Twice a year',
  quarterly: 'Quarterly',
  monthly: 'Monthly',
  per_sale: 'Per sale (event-driven)',
  custom: 'Custom',
};

const ROYALTY_UNIT_VALUES = [
  'per_tree_per_year', 'per_tonne_produced', 'per_kg_produced',
  'percent_of_sale', 'fixed_per_sale', 'other',
] as const;

const REPORTING_CADENCE_VALUES = [
  'annual', 'biannual', 'quarterly', 'monthly', 'per_sale', 'custom',
] as const;

const optionalNumber = z
  .union([z.number(), z.string()])
  .optional()
  .nullable()
  .transform((v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = typeof v === 'string' ? Number(v) : v;
    return Number.isFinite(n) ? n : null;
  });

export const programSchema = z.object({
  organisation_id: z.number().int().positive({ message: 'Owning organisation is required' }),
  name: z.string().min(1, 'Name is required').max(255),
  program_type: z.enum(['annual_tree_royalty', 'production_royalty', 'per_sale_royalty', 'other'], {
    message: 'Program type is required',
  }),
  custom_type_label: z.string().max(100).optional().nullable(),
  description: z.string().optional().nullable(),
  active: z.boolean().optional(),
  royalty_rate: optionalNumber,
  royalty_unit: z.enum(ROYALTY_UNIT_VALUES).optional().nullable(),
  currency: z.string().length(3, 'Use a 3-letter ISO code like AUD').optional().nullable(),
  minimum_royalty: optionalNumber,
  reporting_cadence: z.enum(REPORTING_CADENCE_VALUES).optional().nullable(),
  reporting_due_month: z
    .union([z.number().int(), z.string()])
    .optional()
    .nullable()
    .transform((v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = typeof v === 'string' ? parseInt(v, 10) : v;
      return Number.isFinite(n) ? n : null;
    })
    .refine((v) => v === null || (v >= 1 && v <= 12), { message: 'Month must be 1–12' }),
  reporting_due_day: z
    .union([z.number().int(), z.string()])
    .optional()
    .nullable()
    .transform((v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = typeof v === 'string' ? parseInt(v, 10) : v;
      return Number.isFinite(n) ? n : null;
    })
    .refine((v) => v === null || (v >= 1 && v <= 31), { message: 'Day must be 1–31' }),
  default_terms: z.string().optional().nullable(),
  default_terms_template_id: z
    .union([z.number().int(), z.string()])
    .optional()
    .nullable()
    .transform((v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = typeof v === 'string' ? parseInt(v, 10) : v;
      return Number.isFinite(n) ? n : null;
    }),
  royalty_start_leaf: z
    .union([z.number().int(), z.string()])
    .optional()
    .nullable()
    .transform((v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = typeof v === 'string' ? parseInt(v, 10) : v;
      return Number.isFinite(n) ? n : null;
    })
    .refine((v) => v === null || (v >= 1 && v <= 50), { message: 'Leaf must be 1–50' }),
  reporting_start_leaf: z
    .union([z.number().int(), z.string()])
    .optional()
    .nullable()
    .transform((v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = typeof v === 'string' ? parseInt(v, 10) : v;
      return Number.isFinite(n) ? n : null;
    })
    .refine((v) => v === null || (v >= 1 && v <= 50), { message: 'Leaf must be 1–50' }),
  default_yield_kg_per_tree_per_year:   optionalNumber,
  default_yield_tonnes_per_ha_per_year: optionalNumber,
  rules: z.record(z.string(), z.unknown()).optional(),
  share_with_program_orchards: z.boolean().optional(),
  visibility_config: z.record(z.string(), z.unknown()).optional().nullable(),
  logo_url: z
    .string()
    .trim()
    .url('Must be a valid URL')
    .max(2048)
    .optional()
    .nullable()
    .or(z.literal('')),
  hero_image_url: z
    .string()
    .trim()
    .url('Must be a valid URL')
    .max(2048)
    .optional()
    .nullable()
    .or(z.literal('')),
  tagline: z.string().max(255).optional().nullable(),
  marketing_description: z.string().optional().nullable(),
  website_terms: z.string().optional().nullable(),
  brand_color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour like #FF6600')
    .optional()
    .nullable()
    .or(z.literal('')),
  external_url: z
    .string()
    .trim()
    .url('Must be a valid URL')
    .max(2048)
    .optional()
    .nullable()
    .or(z.literal('')),
  public: z.boolean().optional(),
});

export type ProgramFormData = z.infer<typeof programSchema>;

// Licences are issued only via the template-based flow (POST
// /cultivar/licenses/issue-from-template), so there is no client-side
// "create a licence" Zod schema. The template flow has its own form in
// pages/cultivar/IssueLicense.tsx.

export const declarationSchema = z.object({
  license_id: z.number().int().optional(),
  cultivar_id: z.number().int(),
  season_year: z.number().int().min(2000).max(2100),
  trees_planted: z.number().int().min(0).optional().nullable(),
  hectares_planted: z.number().positive().optional().nullable(),
  rootstock: z.string().max(255).optional().nullable(),
  rootstock_cultivar_id: z.number().int().positive().nullable().optional(),
  interstem_cultivar_id: z.number().int().positive().nullable().optional(),
  planting_date: z.string().optional().nullable(),
  estimated_production_tonnes: z.number().min(0).optional().nullable(),
  actual_production_tonnes: z.number().min(0).optional().nullable(),
  packed_tonnes: z.number().min(0).optional().nullable(),
  pack_formats: z.array(z.object({ format: z.string(), count: z.number() })).optional(),
  marketed_tonnes: z.number().min(0).optional().nullable(),
  markets: z.array(z.object({ market: z.string(), tonnes: z.number() })).optional(),
  notes: z.string().optional().nullable(),
});

export type DeclarationFormData = z.infer<typeof declarationSchema>;

export const guideSchema = z.object({
  title: z.string().min(1, 'Title is required').max(500),
  category: z.enum(['pruning', 'spraying', 'thinning', 'harvest', 'irrigation', 'nutrition', 'pest_management', 'post_harvest', 'general']),
  content: z.string().min(1, 'Content is required'),
  region: z.string().max(255).optional().nullable(),
  season: z.enum(['spring', 'summer', 'autumn', 'winter', 'year_round']).optional().nullable(),
  visibility: z.enum(['public', 'licensed_growers', 'organisation_only']).optional(),
  version: z.string().max(20).optional(),
});

export type GuideFormData = z.infer<typeof guideSchema>;

// =============================================================================
// CULTIVAR ORDERING — PROGRAM PRICE VERSIONS + CONTRACT TEMPLATES
// =============================================================================

export type CultivarContractRole =
  | 'sublicensee_nursery'
  | 'end_customer_grow'
  | 'end_customer_non_prop'
  | 'end_customer_royalty'
  | 'marketing_approval';

export const CULTIVAR_CONTRACT_ROLES: CultivarContractRole[] = [
  'sublicensee_nursery',
  'end_customer_grow',
  'end_customer_non_prop',
  'end_customer_royalty',
  'marketing_approval',
];

export const CULTIVAR_CONTRACT_ROLE_LABELS: Record<CultivarContractRole, string> = {
  sublicensee_nursery:   'Sublicensee nursery (propagation)',
  end_customer_grow:     'End customer — orchard planting licence',
  end_customer_non_prop: 'End customer — Non-Propagation Agreement',
  end_customer_royalty:  'End customer — royalty schedule',
  marketing_approval:    'Marketing / distribution approval',
};

export interface CultivarProgramPriceVersion {
  id: number;
  program_id: number;
  version: number;
  effective_from: string;
  effective_to: string | null;
  royalty_rate: Numeric;
  royalty_unit: string | null;
  currency: string;
  minimum_royalty: Numeric;
  per_tree_fee: Numeric;
  annual_fee: Numeric;
  production_royalty: Record<string, unknown> | null;
  rules: Record<string, unknown> | null;
  notes: string | null;
  created_by_user_id: number | null;
  created_at: string;
}

export interface PriceVersionPublishPayload {
  effective_from?: string | null;
  royalty_rate?: number | null;
  royalty_unit?: string | null;
  currency?: string | null;
  minimum_royalty?: number | null;
  per_tree_fee?: number | null;
  annual_fee?: number | null;
  production_royalty?: Record<string, unknown> | null;
  rules?: Record<string, unknown> | null;
  notes?: string | null;
}

/**
 * A programme role names a contract FAMILY. Orders are sent that family's
 * published version at the time they are dispatched; nothing pins a version.
 */
export interface CultivarProgramContractTemplate {
  id: number;
  program_id: number;
  role: CultivarContractRole;
  family_id: number;
  required: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  family_name: string;
  family_license_tier: string;
  /** Null when the contract has no published version — orders cannot be sent. */
  current_version_id: number | null;
  current_version: number | null;
  current_published_at: string | null;
  current_content_hash: string | null;
}

export interface ContractTemplateUpsertPayload {
  family_id: number;
  required?: boolean | null;
  notes?: string | null;
}

// =============================================================================
// CULTIVAR ORDER API KEYS (PR5)
// =============================================================================

export interface CultivarOrderApiKey {
  id: number;
  regional_license_id: number;
  organisation_id: number;
  label: string;
  key_prefix: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
  cultivar_id?: number;
  cultivar_name?: string;
  region?: string | null;
  country?: string | null;
  // only set on the create response — never persisted server-side
  plaintext?: string;
}

// =============================================================================
// CULTIVAR ORDER APPROVERS (PR4)
// =============================================================================

export type CultivarApproverRole = 'marketing' | 'distribution' | 'breeder' | 'other';

export const CULTIVAR_APPROVER_ROLES: CultivarApproverRole[] = [
  'marketing', 'distribution', 'breeder', 'other',
];

export const CULTIVAR_APPROVER_ROLE_LABELS: Record<CultivarApproverRole, string> = {
  marketing: 'Marketing',
  distribution: 'Distribution',
  breeder: 'Breeder',
  other: 'Other',
};

export interface CultivarProgramApprover {
  id: number;
  program_id: number;
  approver_org_id: number;
  approver_org_name?: string;
  approver_role: CultivarApproverRole;
  required: boolean;
  region: string | null;
  country: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CultivarOrderApproval {
  id: number;
  order_id: number;
  approver_org_id: number;
  approver_org_name?: string;
  approver_role: CultivarApproverRole;
  required: boolean;
  status: 'pending' | 'approved' | 'rejected' | 'skipped';
  decided_by_user_id: number | null;
  decided_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // joined for the approver queue view
  cultivar_name?: string;
  cultivar_protection_status?: 'pending' | 'protected' | 'expired' | 'open' | null;
  nursery_org_name?: string;
  customer_company?: string;
  customer_contact?: string;
  qty_trees?: number;
  frozen_total?: string | number | null;
  currency?: string;
  order_status?: string;
}

// =============================================================================
// CULTIVAR ORDERS — new flow (PR3)
// =============================================================================

export type CultivarOrderStatus =
  | 'draft'
  | 'submitted'
  | 'nursery_accepted'
  | 'awaiting_approvers'
  | 'awaiting_customer'
  | 'customer_signed'
  | 'confirmed'
  | 'fulfilled'
  | 'cancelled'
  | 'accepted';

export const CULTIVAR_ORDER_STATUS_LABELS: Record<CultivarOrderStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  nursery_accepted: 'Nursery accepted',
  awaiting_approvers: 'Awaiting approvers',
  awaiting_customer: 'Awaiting customer signature',
  customer_signed: 'Customer signed',
  confirmed: 'Confirmed',
  fulfilled: 'Fulfilled',
  cancelled: 'Cancelled',
  accepted: 'Accepted (legacy)',
};

export interface CultivarOrderCustomerInput {
  company_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string | null;
  address?: Record<string, unknown> | null;
  notes?: string | null;
}

export interface PlaceOrderPayload {
  cultivar_id: number;
  qty_trees: number;
  customer_id?: number | null;
  customer?: CultivarOrderCustomerInput | null;
  sale_window_start?: string | null;
  sale_window_end?: string | null;
  expected_payment_date?: string | null;
  notes?: string | null;
}

export interface CultivarOrderDocument {
  id: number;
  order_id: number;
  role: CultivarContractRole;
  template_id: number;
  template_version: number;
  required: boolean;
  merged_body: string;
  envelope_id: number | null;
  status: 'draft' | 'sent' | 'signed' | 'declined' | 'void';
  signer_email: string | null;
  signer_name: string | null;
  agreement_id?: string | null;
  envelope_status?: string | null;
  envelope_signer_email?: string | null;
  envelope_signed_at?: string | null;
  signed_pdf_path?: string | null;
}

export interface CultivarOrder {
  id: number;
  nursery_org_id: number;
  orchard_org_id: number | null;
  cultivar_id: number;
  customer_id: number | null;
  nursery_license_id: number | null;
  end_customer_license_id: number | null;
  qty_trees: number;
  status: CultivarOrderStatus;
  price_version_id: number | null;
  frozen_unit_price: Numeric;
  frozen_total: Numeric;
  frozen_annual_fee: Numeric;
  frozen_pricing_snapshot: Record<string, unknown> | null;
  currency: string;
  sale_window_start: string | null;
  sale_window_end: string | null;
  expected_payment_date: string | null;
  invoice_id: number | null;
  source: 'in_platform' | 'api';
  api_key_id: number | null;
  notes: string | null;
  confirmed_at: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  fulfilled_at: string | null;
  created_at: string;
  updated_at: string;
  cultivar_name?: string;
  cultivar_trade_name?: string | null;
  cultivar_protection_status?: 'pending' | 'protected' | 'expired' | 'open' | null;
  customer_company?: string | null;
  customer_contact?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  customer_address?: Record<string, unknown> | null;
  nursery_org_name?: string | null;
  orchard_org_name?: string | null;
  documents?: CultivarOrderDocument[];
  approvals?: CultivarOrderApproval[];
}

// =============================================================================
// PAGINATION
// =============================================================================

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}
